import { getRecord, listRecords, saveRecord } from '@/services/localDb';
import { getOrCreateDeviceId } from '@/services/localDb/device';
import { assertCurrentDeviceCanCreateOfflineActions } from '@/services/device/deviceOperationalPolicy';

export const PENDING_ACTION_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

export const PENDING_ACTION_PROCESSING_LEASE_MS = 120000;

function nowIso() {
  return new Date().toISOString();
}

export function createLocalId(prefix) {
  const uuid = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `${prefix}_${uuid}`;
}

function assertRestaurantScope(restaurantId) {
  if (!restaurantId) {
    throw new Error('No se puede crear una accion pendiente sin restaurant_id.');
  }
}

function isRestaurantAction(action, restaurantId) {
  return String(action?.restaurant_id || '') === String(restaurantId);
}

export async function enqueuePendingAction(action = {}) {
  assertCurrentDeviceCanCreateOfflineActions();
  assertRestaurantScope(action.restaurant_id);

  const timestamp = nowIso();
  const record = {
    id: action.id || createLocalId('action'),
    restaurant_id: String(action.restaurant_id),
    device_id: action.device_id || getOrCreateDeviceId(),
    entity_type: action.entity_type,
    entity_local_id: action.entity_local_id,
    entity_server_id: action.entity_server_id || null,
    action_type: action.action_type,
    payload: action.payload || {},
    idempotency_key: action.idempotency_key || action.client_request_id || action.id || null,
    client_request_id: action.client_request_id || action.idempotency_key || action.id || null,
    status: action.status || PENDING_ACTION_STATUS.PENDING,
    created_at: action.created_at || timestamp,
    updated_at: timestamp,
    retry_count: Number(action.retry_count || 0),
    last_error: action.last_error || null,
    error_type: action.error_type || null,
    error_code: action.error_code || null,
    conflict_payload: action.conflict_payload || null,
    processing_started_at: action.processing_started_at || null,
    processing_owner: action.processing_owner || null,
  };

  record.idempotency_key = record.idempotency_key || record.id;
  record.client_request_id = record.client_request_id || record.id;

  if (!record.entity_type || !record.entity_local_id || !record.action_type) {
    throw new Error('La accion pendiente requiere entity_type, entity_local_id y action_type.');
  }

  const saved = await saveRecord('pending_actions', record);
  if (!saved) {
    throw new Error('No se pudo guardar la accion pendiente en IndexedDB.');
  }
  return saved;
}

export async function listPendingActions(restaurantId) {
  assertRestaurantScope(restaurantId);
  const records = await listRecords('pending_actions');
  return records
    .filter((action) => isRestaurantAction(action, restaurantId))
    .filter((action) => action.status !== PENDING_ACTION_STATUS.COMPLETED)
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
}

export async function getPendingAction(actionId) {
  return getRecord('pending_actions', actionId);
}

async function updatePendingActionStatus(actionId, status, extra = {}) {
  const current = await getPendingAction(actionId);
  if (!current) return null;

  const clearsProcessingLease = status !== PENDING_ACTION_STATUS.PROCESSING;

  return saveRecord('pending_actions', {
    ...current,
    ...extra,
    status,
    processing_started_at: clearsProcessingLease
      ? null
      : (extra.processing_started_at || current.processing_started_at || nowIso()),
    processing_owner: clearsProcessingLease
      ? null
      : (extra.processing_owner || current.processing_owner || null),
    updated_at: nowIso(),
  });
}

export async function markPendingActionProcessing(actionId, processingOwner = getOrCreateDeviceId()) {
  return updatePendingActionStatus(actionId, PENDING_ACTION_STATUS.PROCESSING, {
    processing_started_at: nowIso(),
    processing_owner: processingOwner,
  });
}

export async function markPendingActionPending(actionId, extra = {}) {
  return updatePendingActionStatus(actionId, PENDING_ACTION_STATUS.PENDING, extra);
}

export async function markPendingActionCompleted(actionId) {
  return updatePendingActionStatus(actionId, PENDING_ACTION_STATUS.COMPLETED, {
    last_error: null,
    error_type: null,
    error_code: null,
    conflict_payload: null,
  });
}

export async function markPendingActionFailed(actionId, error) {
  const current = await getPendingAction(actionId);
  const normalizedMessage = error?.message || String(error || 'Error pendiente de sincronizacion.');
  return updatePendingActionStatus(actionId, PENDING_ACTION_STATUS.FAILED, {
    retry_count: Number(current?.retry_count || 0) + 1,
    last_error: normalizedMessage,
    error_type: error?.type || null,
    error_code: error?.code || null,
    conflict_payload: error?.conflict_payload || null,
  });
}

export async function cancelPendingAction(actionId) {
  return updatePendingActionStatus(actionId, PENDING_ACTION_STATUS.CANCELLED);
}

function hasExpiredProcessingLease(action, leaseMs = PENDING_ACTION_PROCESSING_LEASE_MS) {
  if (action?.status !== PENDING_ACTION_STATUS.PROCESSING) return false;

  const startedAt = new Date(action.processing_started_at || action.updated_at || action.created_at || 0).getTime();
  if (!Number.isFinite(startedAt) || startedAt <= 0) {
    return true;
  }

  return (Date.now() - startedAt) >= leaseMs;
}

export async function recoverStaleProcessingActions(restaurantId, options = {}) {
  assertRestaurantScope(restaurantId);

  const leaseMs = Number(options.leaseMs || PENDING_ACTION_PROCESSING_LEASE_MS);
  const processingOwner = options.processingOwner || getOrCreateDeviceId();
  const actions = await listPendingActions(restaurantId);
  const staleActions = actions.filter((action) => hasExpiredProcessingLease(action, leaseMs));

  if (!staleActions.length) {
    return {
      recovered: 0,
      action_ids: [],
    };
  }

  await Promise.all(staleActions.map((action) => markPendingActionPending(action.id, {
    last_error: action.last_error || `Lease de procesamiento recuperado para ${processingOwner}.`,
  })));

  return {
    recovered: staleActions.length,
    action_ids: staleActions.map((action) => action.id),
  };
}

export async function getPendingActionStats(restaurantId) {
  const actions = await listPendingActions(restaurantId);

  return actions.reduce((stats, action) => {
    const key = action.status || 'unknown';
    stats[key] = Number(stats[key] || 0) + 1;
    if (["conflict", "policy"].includes(action.error_type) && action.status === PENDING_ACTION_STATUS.FAILED) {
      stats.conflicts += 1;
    }
    if (action.error_type === 'policy' && action.status === PENDING_ACTION_STATUS.FAILED) {
      stats.policy_blocked += 1;
    }
    stats.total += 1;
    return stats;
  }, {
    total: 0,
    pending: 0,
    processing: 0,
    failed: 0,
    cancelled: 0,
    conflicts: 0,
    policy_blocked: 0,
    unknown: 0,
  });
}

export async function listPendingActionConflicts(restaurantId) {
  const actions = await listPendingActions(restaurantId);
  return actions.filter((action) =>
    action.status === PENDING_ACTION_STATUS.FAILED && ["conflict", "policy"].includes(action.error_type)
  );
}

export async function getPendingActionCount(restaurantId) {
  const actions = await listPendingActions(restaurantId);
  return actions.filter((action) => action.status === PENDING_ACTION_STATUS.PENDING).length;
}

export async function getPendingActionsByEntity(restaurantId, entityType, entityLocalId) {
  const actions = await listPendingActions(restaurantId);
  return actions.filter((action) =>
    action.entity_type === entityType && action.entity_local_id === entityLocalId
  );
}
